import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../Componets/AdminLayout';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

export default function EditSection() {
  const apiUrl = process.env.REACT_APP_API_URL;
  const location = useLocation();
  const [loaded, setLoaded] = useState(false);
  const { id } = location.state || {};
  const navigate = useNavigate();
  const focusInputRef = useRef(null);
  const currentPath = location?.pathname || '';
  const [isAnyQusMarks, setIsAnyQusMarks] = useState(null);
  const [objectives, setObjectives] = useState([]);
  const [examName, setExamName] = useState('');
  const [rows, setRows] = useState([
    {
      no: '',
      question: '',
      orQuestion: '',
      marks: '',
      subjectObjectiveId: '',
      isOtherQuestions: false,
    },
  ]);
  const [otherRows, setOtherRows] = useState([
    {
      no: '',
      question: '',
      orQuestion: '',
      marks: '',
      subjectObjectiveId: '',
      isOtherQuestions: false,
      isOtherSectionQuestion: true,
    },
  ]);
  const [isOtherSectionVisible, setIsOtherSectionVisible] = useState(true);
  const [isAnyQus, setIsAnyQus] = useState(false);
  const [noOfAnyQus, setNoOfAnyQus] = useState(1);

  useEffect(() => {
    const role = localStorage.getItem('role');
    if (role !== 'ADMIN') {
      navigate('/');
    }
    focus();
    fetchObjectives();
  }, [navigate]);

  const fetchObjectives = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(
        `${apiUrl}/api/subject-objective/subject/${id.subjectId}`,
        { headers: { Authorization: token } }
      );
      setObjectives(response.data.subject.subjectObjectives);
    } catch (error) {
      console.error('Error fetching objectives:', error);
      toast.error('Failed to load objectives');
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios
      .get(`${apiUrl}/api/section/${id.id}`, {
        headers: { Authorization: token },
      })
      .then((response) => {
        const section = response.data;
        setExamName(section.name);
        setIsAnyQus(section.isAnyQus);
        setNoOfAnyQus(section.noOfAnyQus);
        setIsAnyQusMarks(section.questions?.[0]?.marks);

        setRows(
          section.questions.map((question) => ({
            no: question.no,
            question: question.text,
            orQuestion: question.text2 || '',
            marks: question.marks.toString(),
            subjectObjectiveId: question.subjectObjectiveId?.toString() || '',
            isOtherQuestions: question.isOtherQuestions,
          }))
        );

        setOtherRows(
          section.otherSectionQuestions.map((question) => ({
            no: question.no,
            question: question.text,
            orQuestion: question.text2 || '',
            marks: question.marks.toString(),
            subjectObjectiveId: question.subjectObjectiveId?.toString() || '',
            isOtherQuestions: question.isOtherQuestions,
            isOtherSectionQuestion: true,
          }))
        );

        setIsOtherSectionVisible(section.otherSectionQuestions.length > 0);
      })
      .catch((error) => {
        console.error('Error fetching section:', error);
        error.response?.status === 401 && navigate('/');
      });
  }, [apiUrl, id, navigate]);

  const focus = () => {
    if (focusInputRef.current) {
      focusInputRef.current.focus();
    }
  };

  const handleSwitchChange = (e) => {
    setIsAnyQus(e.target.checked);
    if (e.target.checked === false) {
      setIsAnyQusMarks(null);
    }
  };

  const handleNoOfAnyQusChange = (e) => {
    setNoOfAnyQus(e.target.value);
  };

  const handleSubmit = (e) => {
    setLoaded(true);
    e.preventDefault();

    const mainSectionTotalMarks = rows.reduce(
      (acc, row) => acc + parseInt(row.marks || 0, 10),
      0
    );
    const otherSectionTotalMarks = otherRows.reduce(
      (acc, row) => acc + parseInt(row.marks || 0, 10),
      0
    );

    if (
      isOtherSectionVisible &&
      otherSectionTotalMarks !== mainSectionTotalMarks
    ) {
      toast.error(
        'Other Section total marks should be equal to Main Section total marks'
      );
      setLoaded(false);
      return;
    }

    const token = localStorage.getItem('token');
    const data = {
      name: examName,
      subjectId: parseInt(id.subjectId, 10),
      examId: parseInt(id.examId, 10),
      questions: rows.map((row) => ({
        no: row.no,
        text: row.question,
        text2: row.orQuestion,
        marks: parseInt(row.marks, 10),
        subjectObjectiveId: row.subjectObjectiveId
          ? parseInt(row.subjectObjectiveId, 10)
          : null,
        isOtherQuestions: row.isOtherQuestions,
      })),
      otherSectionQuestions: otherRows.map((row) => ({
        no: row.no,
        text: row.question,
        text2: row.orQuestion,
        marks: parseInt(row.marks, 10),
        subjectObjectiveId: row.subjectObjectiveId
          ? parseInt(row.subjectObjectiveId, 10)
          : null,
        isOtherQuestions: row.isOtherQuestions,
        isOtherSectionQuestion: true,
      })),
      isAnyQus: isAnyQus,
      noOfAnyQus: isAnyQus ? parseInt(noOfAnyQus, 10) : 0,
    };

    axios
      .put(`${apiUrl}/api/section/${id.id}`, data, {
        headers: { Authorization: token },
      })
      .then((response) => {
        setLoaded(false);
        toast.success('Section updated successfully');
        focus();
        if (currentPath === '/EditSection') {
          setTimeout(() => {
            if (window.location.pathname === '/EditSection') {
              navigate(-1);
            }
          }, 3000);
        }
      })
      .catch((error) => {
        setLoaded(false);
        console.error('Error updating section:', error);
        toast.error(error.response?.data?.message || 'Error updating section');
        error.response?.status === 401 && navigate('/');
      });
  };

  const handleChange = (e) => {
    setExamName(e.target.value);
  };

  const handleRowChange = (e, index) => {
    const { name, value, checked, type } = e.target;
    const updatedRows = [...rows];
    if (name === 'marks') {
      setIsAnyQusMarks(value);
    }
    updatedRows[index][name] = type === 'checkbox' ? checked : value;
    setRows(updatedRows);
  };

  const handleOtherRowChange = (e, index) => {
    const { name, value, checked, type } = e.target;
    const updatedRows = [...otherRows];
    if (name === 'marks') {
      setIsAnyQusMarks(value);
    }
    updatedRows[index][name] = type === 'checkbox' ? checked : value;
    setOtherRows(updatedRows);
  };

  const addRow = () => {
    setRows([
      ...rows,
      {
        no: '',
        question: '',
        orQuestion: '',
        marks: '',
        subjectObjectiveId: '',
        isOtherQuestions: false,
      },
    ]);
    if (isAnyQus) {
      setRows((prevRows) =>
        prevRows.map((row) => ({
          ...row,
          marks: isAnyQusMarks,
        }))
      );
    }
  };

  const addOtherRow = () => {
    setOtherRows([
      ...otherRows,
      {
        no: '',
        question: '',
        orQuestion: '',
        marks: '',
        subjectObjectiveId: '',
        isOtherQuestions: false,
        isOtherSectionQuestion: true,
      },
    ]);
  };

  const deleteRow = (index) => {
    const updatedRows = [...rows];
    updatedRows.splice(index, 1);
    setRows(updatedRows);
  };

  const deleteOtherRow = (index) => {
    const updatedRows = [...otherRows];
    updatedRows.splice(index, 1);
    setOtherRows(updatedRows);
  };

  useEffect(() => {
    if (isAnyQus) {
      setRows((prevRows) =>
        prevRows.map((row) => ({
          ...row,
          marks: isAnyQusMarks,
        }))
      );
    }
  }, [isAnyQus, isAnyQusMarks]);

  return (
    <AdminLayout ac4="active">
      <main id="main" className="main">
        <div className="pagetitle">
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/AdminHome">Dashboard</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/ListExam">View Exam</Link>
              </li>
              <li
                className="breadcrumb-item cursor-pointer"
                onClick={() => navigate(-2)}
              >
                Exam Subjects
              </li>
              <li
                className="breadcrumb-item cursor-pointer"
                onClick={() => navigate(-1)}
              >
                List Section
              </li>
              <li className="breadcrumb-item active">Edit Section</li>
            </ol>
          </nav>
        </div>
        <section className="section col-lg-12">
          <div className="row">
            <div className="">
              <div className="card">
                <div className="card-body mt-5 mb-1 mx-3">
                  <form onSubmit={handleSubmit}>
                    <div className="container text-center px-1">
                      <div className="row row-cols-12 mb-4 text-center">
                        <div className="col-2">
                          <div className="form-floating">
                            <input
                              className="form-control"
                              type="number"
                              name="totalMarks"
                              value={
                                isAnyQus
                                  ? noOfAnyQus * parseInt(isAnyQusMarks || 0)
                                  : rows.reduce(
                                      (acc, row) =>
                                        acc + parseInt(row.marks || 0),
                                      0
                                    )
                              }
                              disabled
                              placeholder="Total Marks"
                              required
                            />
                            <label className="form-label">
                              Total <span className="text-danger">*</span>
                            </label>
                          </div>
                        </div>
                        <div className="col-5">
                          <div className="form-floating">
                            <input
                              className="form-control"
                              type="text"
                              name="examName"
                              value={examName}
                              onChange={handleChange}
                              placeholder="Exam Name"
                              required
                              ref={focusInputRef}
                            />
                            <label className="form-label">
                              Section Name{' '}
                              <span className="text-danger">*</span>
                            </label>
                          </div>
                        </div>
                        <div className="col-2 mx-1 mt-3">
                          <input
                            className="form-check-input mt-0"
                            type="checkbox"
                            checked={isAnyQus}
                            onChange={handleSwitchChange}
                            disabled
                            style={{ width: '25px', height: '25px' }}
                          />
                          <label className="form-label ms-2">
                            Any Question
                          </label>
                        </div>
                        <div className="col-2" style={{ marginTop: '-7px' }}>
                          {isAnyQus && (
                            <div className="col-12">
                              <div className="form-floating px-1">
                                <input
                                  className="form-control"
                                  type="text"
                                  value={noOfAnyQus}
                                  onChange={handleNoOfAnyQusChange}
                                  min="1"
                                  placeholder="No"
                                  required
                                />
                                <label className="form-label">
                                  Any Question{' '}
                                  <span className="text-danger">*</span>
                                </label>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <hr style={{ width: '104%', marginLeft: '-2%' }} />
                    {rows.map((row, index) => (
                      <div key={index} className="row mb-2">
                        <div className="container text-center">
                          <div className="row justify-content-md-center">
                            <div className="container">
                              <div className="row row-cols-5 gap-3">
                                <div className="col-2">
                                  <div className="form-floating">
                                    <input
                                      className="form-control"
                                      type="text"
                                      name="no"
                                      value={row.no}
                                      onChange={(e) =>
                                        handleRowChange(e, index)
                                      }
                                      placeholder="No"
                                      required
                                    />
                                    <label className="form-label">
                                      No<span className="text-danger">*</span>
                                    </label>
                                  </div>
                                </div>
                                <div className="col-3">
                                  <div className="form-floating">
                                    <select
                                      className="form-select"
                                      name="subjectObjectiveId"
                                      value={row.subjectObjectiveId}
                                      onChange={(e) =>
                                        handleRowChange(e, index)
                                      }
                                    >
                                      <option value="">Select Objective</option>
                                      {objectives.map((obj) => (
                                        <option key={obj.id} value={obj.id}>
                                          {obj.objectiveName}
                                        </option>
                                      ))}
                                    </select>
                                    <label className="form-label">
                                      Objective
                                    </label>
                                  </div>
                                </div>
                                <div className="col-1 mt-2">
                                  <div className="d-flex align-items-center justify-content-center">
                                    <input
                                      className="form-check-input mt-0"
                                      type="checkbox"
                                      disabled={true}
                                      checked={true}
                                      style={{ width: '25px', height: '25px' }}
                                    />
                                    <label className="form-label ms-2 mt-2">
                                      Q
                                    </label>
                                  </div>
                                </div>
                                <div className="col-2 mt-2">
                                  <div className="d-flex align-items-center justify-content-center">
                                    <input
                                      className="form-check-input mt-0"
                                      type="checkbox"
                                      name="isOtherQuestions"
                                      hidden={isAnyQus || isOtherSectionVisible}
                                      checked={row.isOtherQuestions}
                                      onChange={(e) =>
                                        handleRowChange(e, index)
                                      }
                                      style={{ width: '25px', height: '25px' }}
                                    />
                                    <label
                                      className="form-label ms-2 mt-2"
                                      hidden={isAnyQus || isOtherSectionVisible}
                                    >
                                      Or Q
                                    </label>
                                  </div>
                                </div>
                                <div className="col-2">
                                  <div className="form-floating">
                                    <input
                                      className="form-control"
                                      type="text"
                                      name="marks"
                                      value={row.marks}
                                      onChange={(e) =>
                                        handleRowChange(e, index)
                                      }
                                      placeholder="Marks"
                                      required
                                      disabled={
                                        index > 0
                                          ? isAnyQus && isAnyQusMarks
                                          : false
                                      }
                                    />
                                    <label className="form-label">
                                      Marks
                                      <span className="text-danger">*</span>
                                    </label>
                                  </div>
                                </div>
                                <div className="col-1">
                                  <div className="col-sm mt-2">
                                    <button
                                      type="button"
                                      className="btn btn-danger"
                                      onClick={() => deleteRow(index)}
                                    >
                                      <i className="bi bi-trash-fill"></i>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    <hr style={{ width: '104%', marginLeft: '-2%' }} />
                    <div className="d-flex justify-content-end">
                      <button
                        className="btn btn-primary mt-1 mb-2 mx-2"
                        type="button"
                        onClick={addRow}
                      >
                        <i className="bi bi-journal-plus"></i>
                      </button>
                    </div>
                    {isOtherSectionVisible && (
                      <>
                        <hr style={{ width: '104%', marginLeft: '-2%' }} />
                        {otherRows.map((row, index) => (
                          <div key={index} className="row mb-2">
                            <div className="container text-center">
                              <div className="row justify-content-md-center">
                                <div className="container">
                                  <div className="row row-cols-5 gap-3">
                                    <div className="col-2">
                                      <div className="form-floating">
                                        <input
                                          className="form-control"
                                          type="text"
                                          name="no"
                                          value={row.no}
                                          onChange={(e) =>
                                            handleOtherRowChange(e, index)
                                          }
                                          placeholder="No"
                                          required
                                        />
                                        <label className="form-label">
                                          No
                                          <span className="text-danger">*</span>
                                        </label>
                                      </div>
                                    </div>
                                    <div className="col-3">
                                      <div className="form-floating">
                                        <select
                                          className="form-select"
                                          name="subjectObjectiveId"
                                          value={row.subjectObjectiveId}
                                          onChange={(e) =>
                                            handleOtherRowChange(e, index)
                                          }
                                        >
                                          <option value="">
                                            Select Objective
                                          </option>
                                          {objectives.map((obj) => (
                                            <option key={obj.id} value={obj.id}>
                                              {obj.objectiveName}
                                            </option>
                                          ))}
                                        </select>
                                        <label className="form-label">
                                          Objective
                                        </label>
                                      </div>
                                    </div>
                                    <div className="col-1 mt-2">
                                      <div className="d-flex align-items-center justify-content-center">
                                        <input
                                          className="form-check-input mt-0"
                                          type="checkbox"
                                          disabled={true}
                                          checked={true}
                                          style={{
                                            width: '25px',
                                            height: '25px',
                                          }}
                                        />
                                        <label className="form-label ms-2 mt-2">
                                          Q
                                        </label>
                                      </div>
                                    </div>
                                    <div className="col-2 mt-2">
                                      <div className="d-flex align-items-center justify-content-center">
                                        <input
                                          className="form-check-input mt-0"
                                          type="checkbox"
                                          name="isOtherQuestions"
                                          hidden={
                                            isAnyQus || isOtherSectionVisible
                                          }
                                          checked={row.isOtherQuestions}
                                          onChange={(e) =>
                                            handleOtherRowChange(e, index)
                                          }
                                          style={{
                                            width: '25px',
                                            height: '25px',
                                          }}
                                        />
                                        <label
                                          className="form-label ms-2 mt-2"
                                          hidden={
                                            isAnyQus || isOtherSectionVisible
                                          }
                                        >
                                          Or Q
                                        </label>
                                      </div>
                                    </div>
                                    <div className="col-2">
                                      <div className="form-floating">
                                        <input
                                          className="form-control"
                                          type="text"
                                          name="marks"
                                          value={row.marks}
                                          onChange={(e) =>
                                            handleOtherRowChange(e, index)
                                          }
                                          placeholder="Marks"
                                          required
                                        />
                                        <label className="form-label">
                                          Marks
                                          <span className="text-danger">*</span>
                                        </label>
                                      </div>
                                    </div>
                                    <div className="col-1">
                                      <div className="col-sm mt-2">
                                        <button
                                          type="button"
                                          className="btn btn-danger"
                                          onClick={() => deleteOtherRow(index)}
                                        >
                                          <i className="bi bi-trash-fill"></i>
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                        <hr style={{ width: '104%', marginLeft: '-2%' }} />
                        <div className="d-flex justify-content-end">
                          <button
                            className="btn btn-primary mt-1 mb-2 mx-2"
                            type="button"
                            onClick={addOtherRow}
                          >
                            <i className="bi bi-journal-plus"></i>
                          </button>
                        </div>
                      </>
                    )}
                    <div className="row mb-3">
                      <div className="col mx-2 text-end">
                        <button
                          type="button"
                          className="btn btn-primary px-3"
                          onClick={() => navigate(-1)}
                        >
                          <i className="bi bi-arrow-bar-left"></i>Back
                        </button>
                        <button
                          type="submit"
                          className="btn btn-primary px-4 mx-2"
                          disabled={loaded}
                        >
                          {loaded ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm"
                                role="status"
                                aria-hidden="true"
                              ></span>{' '}
                              wait...
                            </>
                          ) : (
                            <>Save</>
                          )}
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <ToastContainer position="top-center" style={{ width: 'auto' }} />
    </AdminLayout>
  );
}
